import { ICCalendarIcon } from '@village/icons';
import { Link } from '@village/ui';
import { SchedulingUnavailableReason, useAppOptions, useCaptureCountlyEvent } from 'hooks';
import { equals } from 'ramda';
import { FC, Fragment, useEffect, useMemo } from 'react';
import { DepartmentHubspotInfo } from 'types';

import CallButton from './CallButton';
import * as Styled from './styles';

interface Props {
    reasons: SchedulingUnavailableReason[];
    location?: Partial<DepartmentHubspotInfo> | null;
    compact?: boolean;
}

const SchedulingNotAvailable: FC<Props> = ({ location, reasons, compact = false }) => {
    const appOptions = useAppOptions();
    const { addCountlyEvent } = useCaptureCountlyEvent();

    const phoneNumber = appOptions.type === 'locationWidget' ? appOptions?.phone : location?.phone;
    const onlyDisabledForNewPatients = equals(reasons, ['NO_REASONS_NEW_PATIENTS']);

    const { title, subTitle } = useMemo(() => {
        if (compact) {
            return {
                title: null,
                subTitle: 'Online scheduling is not available for this provider. Please call to book.',
            };
        }

        if (appOptions.type === 'locationWidget') {
            if (onlyDisabledForNewPatients) {
                return {
                    title: 'No providers at this location are scheduling new patients.',
                    subTitle: (
                        <Fragment>
                            To find availability for providers at another clinic close to you,{' '}
                            <Link href="/locator">click here</Link>
                        </Fragment>
                    ),
                };
            }

            return {
                title: 'Online scheduling is not currently available at this location',
                subTitle: "We're here to help. Please call to book.",
            };
        }

        if (onlyDisabledForNewPatients) {
            return {
                title: 'This provider is not scheduling new patients.',
                subTitle: location ? null : 'To schedule an appointment, please call the clinic phone number.',
            };
        }

        return {
            title: 'Online scheduling is not currently available for this provider',
            subTitle: "We're here to help. Please call to book.",
        };
    }, [reasons, appOptions.type, phoneNumber, addCountlyEvent, location]);

    useEffect(() => {
        addCountlyEvent({
            key: 'schedulingNotAvailable',
            segmentation: {
                reason: reasons.join(', '),
                providerId: appOptions.type === 'providerWidget' ? appOptions.providerNpi : undefined,
                departmentId: appOptions.type === 'locationWidget' ? appOptions.departmentId : undefined,
            },
        });
    }, [reasons, appOptions]);

    return (
        <Styled.Container>
            {!compact && (
                <Styled.Icon>
                    <ICCalendarIcon />
                </Styled.Icon>
            )}
            {title && <Styled.Title type="sub4">{title}</Styled.Title>}
            <Styled.SubTitle type="body1">{subTitle}</Styled.SubTitle>
            {phoneNumber && <CallButton phoneNumber={phoneNumber} />}
        </Styled.Container>
    );
};

export { SchedulingNotAvailable };
